// Les points d'entrée des fonctions du bureau (D-126) et des reçus fiscaux (D-124), regroupés ici : chaque entrée dit son
// module (D-122) — bénévolat (frais des bénévoles), partenaires (dons), les deux (registre, signature), ou le socle (le
// bureau, « Qui contacter ») — et passe par `entryOn`, qui lit les modules activés du club (comme module_on côté base).
// D-132 : une seule entrée des reçus fiscaux côté bureau, la tuile « Reçus fiscaux » de « Financer le club » (le registre :
// les reçus à signer, les déclarations à valider) ; ses réglages, la ligne « ⚙ Réglages : Reçus » ; les kilomètres des
// conducteurs quittent l'onglet Bénévolat › Groupes pour l'écran des reçus fiscaux (`renderReceiptsWithKm`).
import { myFunctions, mySignature, receiptsBoard, type MyFunction } from '../lib/recus-api';
import { isMinorAccount, modOn, personaOf, type Ctx } from './context';
import { vouvoie } from './vous';
import { html, mount, type Html } from './dom';
import { openSignatureSheet, renderReceiptSettings } from './recus';

export type EntryModule = 'benevolat' | 'partenaires';
/** Le module de chaque entrée ; [] : socle (toujours là). */
export const ENTRY_MODULES = {
  trajets: ['benevolat'],                    // Moi › Mes trajets pour le club
  dons: ['partenaires'],                     // fiche d'un partenaire › Noter un don
  registre: ['benevolat', 'partenaires'],    // Club › Reçus fiscaux (l'un ou l'autre)
  signature: ['benevolat', 'partenaires'],   // Moi › Ma signature (signataires)
  bureau: [],                                // Club › Le bureau
  contacter: [],                             // Club › Qui contacter
} satisfies Record<string, EntryModule[]>;
export type Entry = keyof typeof ENTRY_MODULES;

/** L'entrée est-elle affichée ? Socle : toujours ; sinon, un de ses modules est activé pour le club (D-122). */
export function entryOn(ctx: Ctx, e: Entry): boolean {
  const mods = ENTRY_MODULES[e] as readonly EntryModule[];
  return !mods.length || mods.some(m => modOn(ctx, m));
}

const row = (href: string, icon: string, title: string, sub: string, guide = '', id = '') => html`
  <a class="card card-row" href="${href}" ${guide ? html`data-guide="${guide}"` : ''} ${id ? html`id="${id}"` : ''}>
    <span class="mi-ico" aria-hidden="true"><span class="ti ${icon}"></span></span>
    <span class="card-main"><strong>${title}</strong><span class="muted small">${sub}</span></span>
    <span class="occ-go" aria-hidden="true">›</span></a>`;
const tile = (href: string, icon: string, title: string, sub: string, hot = false, guide = ''): Html => html`
  <a class="hub-tile ${hot ? 'hot' : ''}" href="${href}" ${guide ? html`data-guide="${guide}"` : ''}>
    <span class="ti ${icon}" aria-hidden="true"></span><strong>${title}</strong><small>${sub}</small></a>`;

/** Un compte adulte du club (ni mineur, ni partenaire seul, ni abonné) : les frais de route, « Qui contacter ». */
const adultMember = (ctx: Ctx) => !!ctx.club && !isMinorAccount(ctx) && !vouvoie(ctx) && personaOf(ctx) !== 'abonne'
  && (ctx.full.people.length > 0 || ctx.full.adminOf.length > 0 || ctx.roles.some(r => r.club_id === ctx.club!.id) || ctx.full.coachOf.size > 0);

let fnCache: { club: string; at: number; list: MyFunction[] } | null = null;
/** Mes fonctions au bureau du club affiché (gardées une minute). */
export async function myClubFunctions(ctx: Ctx): Promise<MyFunction[]> {
  if (!ctx.club) return [];
  if (fnCache && fnCache.club === ctx.club.id && Date.now() - fnCache.at < 60_000) return fnCache.list;
  const list = await myFunctions(ctx.club.id).catch(() => [] as MyFunction[]);
  fnCache = { club: ctx.club.id, at: Date.now(), list };
  return list;
}
export const forgetMyFunctions = () => { fnCache = null; };

/** Moi : « Mes trajets pour le club » (adultes, bénévolat) et « Ma signature » (signataires). */
export async function renderMeEntries(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!adultMember(ctx)) { mount(el, html``); return; }
  const fns = await myClubFunctions(ctx);
  const signer = fns.some(f => f.can_sign);
  // D-131 : enregistrée (vue telle qu'elle se pose sur un reçu, remplaçable) ou à enregistrer.
  const sig = signer && entryOn(ctx, 'signature') ? await mySignature().catch(() => null) : null;
  mount(el, html`
    ${entryOn(ctx, 'trajets') ? row('#/moi/trajets', 'ti-flag', 'Mes trajets pour le club', 'Les km laissés au club : un reçu fiscal', 'me-trajets') : ''}
    ${modOn(ctx, 'tresorerie') ? row('#/moi/depenses', 'ti-receipt', 'Mes dépenses pour le club', 'Un ticket en photo : remboursé, ou en don', 'me-depenses') : ''}
    ${signer && entryOn(ctx, 'signature') ? html`<button type="button" class="card card-row me-look" id="me-sig" data-guide="me-signature">
      <span class="mi-ico" aria-hidden="true"><span class="ti ti-pen"></span></span>
      <span class="card-main"><strong>Ma signature</strong><span class="muted small">${sig ? 'Enregistrée · privée : la voir, la remplacer' : 'À enregistrer pour signer les reçus fiscaux · privée'}</span></span>
      <span class="occ-go" aria-hidden="true">›</span></button>` : ''}`);
  el.querySelector('#me-sig')?.addEventListener('click', () => void openSignatureSheet(ctx, () => void renderMeEntries(el, ctx)));
}

/** Club (bureau) : « Le bureau » (Gérer les adhérents) et « Reçus fiscaux » (Financer le club : le registre, les reçus à
 *  signer, les déclarations des bénévoles à valider). */
export function bureauHubTiles(ctx: Ctx, pendingSign = 0, pendingValidate = 0): { membres: Html; registre: Html | '' } {
  const todo = [pendingSign ? `${pendingSign} à signer` : '', pendingValidate ? `${pendingValidate} à valider` : ''].filter(Boolean).join(' · ');
  return {
    membres: tile('#/club/bureau', 'ti-badge', 'Le bureau', 'Fonctions, qui reçoit quoi', false, 'hub-bureau'),
    registre: entryOn(ctx, 'registre') ? tile('#/club/registre', 'ti-receipt', 'Reçus fiscaux', todo || 'Registre, déclaration annuelle', !!todo, 'hub-registre') : '',
  };
}

/** Les reçus fiscaux sans le bénévolat ni les partenaires (B24, D-122) : pas l'écran d'un module, puisqu'ils viennent avec
 *  l'un ou l'autre ; rien n'est supprimé, le registre revient tel quel. */
export function renderReceiptsOff(root: HTMLElement, ctx: Ctx): void {
  const admin = !!ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id);
  mount(root, html`<section class="screen"><a class="back" href="#/club">‹ Le club</a>
    <header class="screen-head"><p class="eyebrow">Financer le club</p><h2>Reçus fiscaux</h2></header>
    <div class="empty mo-off"><p class="empty-title">Le club n’émet pas de reçu fiscal pour l’instant.</p>
      <p>Les reçus viennent avec le bénévolat (les frais de route laissés au club) ou les partenaires (les dons). Rien n’est supprimé : le registre revient tel quel.</p>
      ${admin ? html`<span class="btn-row"><a class="btn btn-primary" href="#/club/modules">Voir les modules</a></span>` : ''}</div></section>`);
}

/** Club (membres adultes) : « Qui contacter ». */
export async function memberHubTiles(ctx: Ctx): Promise<Html | ''> {
  if (!adultMember(ctx)) return '';
  return tile('#/club/contacter', 'ti-inbox', 'Qui contacter', 'Écrire au bureau', false, 'hub-contacter');
}

/** Club (membres adultes qui tiennent une fonction, hors bureau) : la rubrique « Ma fonction » (K8) — les messages reçus
 *  par mes fonctions, le registre pour un signataire. */
export async function functionHubTiles(ctx: Ctx): Promise<Html | ''> {
  if (!adultMember(ctx)) return '';
  const fns = await myClubFunctions(ctx);
  if (!fns.length) return '';
  const signer = fns.some(f => f.can_sign);
  return html`${tile('#/club/messages', 'ti-inbox', 'Messages reçus', fns.map(f => f.title).join(', '), false, 'hub-messages')}
    ${signer && entryOn(ctx, 'registre') ? tile('#/club/registre', 'ti-receipt', 'Reçus fiscaux', 'Les reçus à signer') : ''}`;
}

/** Les réglages des reçus (#/club/recus), puis les kilomètres des conducteurs de la saison (D-069, partis de Bénévolat ›
 *  Groupes, D-132) quand le bénévolat est activé. */
export async function renderReceiptsWithKm(root: HTMLElement, ctx: Ctx, open?: string): Promise<void> {
  await renderReceiptSettings(root, ctx, open);   // D-139 : les kilomètres des conducteurs sont une rubrique des réglages
}
